import { createServerFn } from '@tanstack/react-start';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { EduBridgeApiTyped } from '@/lib/http.server';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertSelfOrRole } from '@/middlewares/userAuthMiddleware';

import { Enrollment, UserCourseEnrollment } from '../types/enrollments';

export type GetUserEnrollmentsResponse = {
  success: boolean;
  data: Enrollment[];
  error?: string;
};

const fetchUserEnrollments = async (
  edubridgeApiTyped: EduBridgeApiTyped,
  onerosterUserId: string
) => {
  const response = await edubridgeApiTyped.GET(
    '/edubridge/enrollments/user/{userId}',
    {
      params: {
        path: { userId: onerosterUserId },
      },
    }
  );
  return response.data;
};

const serializeEnrollments = (
  enrollments: UserCourseEnrollment[],
  onerosterUserId: string
) => {
  return enrollments
    .map((enrollment) => ({
      id: enrollment.id,
      userId: onerosterUserId,
      courseId: enrollment.course.id,
      courseTitle: enrollment.course.title,
      courseSubjects: enrollment.course.subjects ?? [],
      courseGrades: enrollment.course.grades ?? [],
      primaryAppDomains: enrollment.course.primaryApp?.domains ?? [],
      beginDate: enrollment.beginDate,
      endDate: enrollment.endDate,
      metadata: enrollment.metadata,
      testOutSupported: enrollment.testOutSupported ?? false,
      testOutEligible: enrollment.testOutEligible ?? false,
    }))
    .sort((a, b) => {
      // begin date is optional, if no begin date put at the end, otherwise show newest first
      if (!a.beginDate) return 1;
      if (!b.beginDate) return -1;
      return new Date(b.beginDate).getTime() - new Date(a.beginDate).getTime();
    });
};

export const getUserEnrollments = createServerFn({
  method: 'GET',
})
  .middleware([standardMiddlewares])
  .validator(
    zodValidator(
      z.object({
        onerosterUserId: z.string(),
      })
    )
  )
  .handler(async ({ context, data }) => {
    const { cognitoUser, edubridgeApiTyped, oneRosterApiTyped } = context;
    const { onerosterUserId } = data;
    await assertSelfOrRole(
      cognitoUser,
      oneRosterApiTyped,
      onerosterUserId,
      ELEVATED_ROLES
    );

    const response = await fetchUserEnrollments(
      edubridgeApiTyped,
      onerosterUserId
    );

    const serializedEnrollments = serializeEnrollments(
      response?.data ?? [],
      onerosterUserId
    );

    return {
      success: true,
      data: serializedEnrollments,
    } as GetUserEnrollmentsResponse;
  });
